import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { classNames } from "@/utils/classNames";

interface MappingFieldRowProps {
	foundField: string;
	isMapped: boolean;
	options: AutoCompleteItem[];
	selectedField?: AutoCompleteItem;
	/** First data row's value for this column — shown as the per-row preview (prototype). */
	previewValue?: string;
	/** Rendered next to the column name — e.g. the tracker's "Required" marker. */
	badge?: ReactNode;
	/** Tints the row; used to point at the one column a flow insists on. */
	highlight?: boolean;
	/**
	 * Show the preview even while the column is unassigned. The upload flows differ
	 * here on purpose: for CSV enrichment "Not mapped" tells the user the column
	 * will be dropped, while a tracker file is often exported with headers like
	 * "Field 4", where the value is the only way to tell which column to map.
	 */
	showPreviewWhenUnmapped?: boolean;
	/**
	 * Portal the menu instead of floating it. Required inside a modal or any
	 * `overflow-*` container, which would otherwise clip the open menu
	 * (apps/web/CLAUDE.md §11).
	 */
	portalMenu?: boolean;
	handleSelect: (value?: AutoCompleteItem) => void;
}

/**
 * One row of an upload mapping table: source column, "Import as" dropdown, and the first-row
 * preview value ("Not mapped" while the column is unassigned) — per the PREC-3536 prototype.
 *
 * Shared: the CSV/XLSX enrichment upload and the Profile Tracker source upload map columns the
 * same way, so they map them through the same row instead of each drawing its own table.
 */
export const MappingFieldRow: FunctionComponent<MappingFieldRowProps> = ({
	foundField,
	isMapped,
	options,
	selectedField,
	previewValue,
	badge,
	highlight,
	showPreviewWhenUnmapped,
	portalMenu,
	handleSelect,
}) => {
	const { t } = useTranslation();

	const showsPreview = isMapped || (showPreviewWhenUnmapped && !!previewValue);

	return (
		<li
			className={classNames(
				"text-xs flex flex-row mb-2 justify-between items-center gap-2",
				highlight ? "rounded-md bg-status-warning-light/40 px-2 py-1" : ""
			)}
		>
			<div className="flex flex-wrap items-center gap-2 text-sm break-words">
				{foundField}
				{badge}
			</div>
			<div className="flex flex-row items-center gap-2">
				<div className="min-w-[250px] max-w-[250px] w-[250px]">
					<Dropdown
						classNameButton={classNames(selectedField?.id === "" ? "text-gray-200" : "")}
						data={options}
						defaultValue={selectedField || { title: "Please select", id: "" }}
						floating={!portalMenu}
						handleSelect={handleSelect}
						portal={portalMenu}
					/>
				</div>
				<div
					className={classNames(
						"min-w-[160px] max-w-[160px] truncate text-sm",
						showsPreview ? "text-text-meta" : "italic text-gray-200"
					)}
					data-test="mapping-row-preview"
				>
					{showsPreview ? previewValue : t("csvEnrichment.titleGate.notMapped")}
				</div>
			</div>
		</li>
	);
};
